





































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import VueRouter, { Location } from "vue-router";
import { CardIssueConfig,
         CardPlasticType,
         cardPlasticTypeValueMap,
         SetCardCriteriaCustomizationRequest,
         CreateCardIssueCriteriaRequest,
         CardCustomizationModel,
         EmployeeDisposition } from "@/admin/types";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import CreateCardIssuanceCriteriaForm from "@/admin/components/partners/CreateCardIssuanceCriteriaForm.vue";
import { usePartnerCardIssueCriteriaStore } from "@/admin/store/partnerCardIssueCriteria";
import { CardProvider, UserPermission } from "@/shared/types";
import { useNotifierStore } from "@/shared/store/notifier";
import { usePartnerStore } from "@/admin/store/partner";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import CardSettings from "@/shared/components/CardSettings.vue";
import { useRolePermissionsStore } from "@/shared/store/rolePermissions";

interface EmployerTableItem {
  employerId: number;
  employerName: string;
  issueCardCriteriaId: number;
  status: ""|"Active"|"Inactive"|"Pending";
}

interface UserTableItem {
  employerId: number;
  employerName: string;
  firstName: string;
  lastName: string;
  userEmail: string;
  userSsnLast4: string;
  issueCardCriteriaId: number;
  dateEnabled: Date;
  status: ""|"Active"|"Inactive"|"Pending";
  cardPlasticType: CardPlasticType|null;
}

@Component({
  components: {
    GenericLoadingSpinner,
    CreateCardIssuanceCriteriaForm,
    CardSettings
  },
})
export default class PartnerCardSettings extends Vue {
  $router: VueRouter;
  partnerCardIssueCriteriaStore = usePartnerCardIssueCriteriaStore();
  notifierStore = useNotifierStore();
  partnerStore = usePartnerStore();
  featureFlagStore = useFeatureFlagStore();
  rolePermissionsStore = useRolePermissionsStore();
  employerTableItems: null | EmployerTableItem[] = null;
  userTableItems: null | UserTableItem[] = null;
  isLoaded = this.partnerCardIssueCriteriaStore.isLoaded;
  selectedPartnerCardPlasticType: null | CardPlasticType = null;
  cardPlasticTypeSelected = CardPlasticType.MagneticStrip;

  @Prop() partnerId: string;

  @Prop() routeToPartnerList: () => Location;

  @Watch("partnerId")
  onPartnerIdChanged () {
    this.partnerCardIssueCriteriaStore.setPartnerId(this.partnerIdNum);
    this.partnerCardIssueCriteriaStore.loadPartnerCardIssueStore(this.partnerIdNum);
  }

  @Watch("partnerCardIssueCriteriaStore.cardIssueConfigs")
  transformCardIssueConfigs () {
    if (!this.partnerCardIssueCriteriaStore.isLoaded || !this.partnerCardIssueCriteriaStore.cardIssueConfigs) {
      this.userTableItems = null;
      return;
    }

    this.selectedPartnerCardPlasticType = this.partnerCardPlasticType;

    this.employerTableItems = this.partnerCardIssueCriteriaStore.cardIssueConfigs.employers
      .map(er => ({
        employerId: er.employerId,
        employerName: this.partnerCardIssueCriteriaStore.getEmployerName(er.employerId),
        issueCardCriteriaId: er.issueCardCriteriaId,
        status: this.partnerCardIssueCriteriaStore.getConfigStatus(er),
        dateEnabled: er.dateEnabled,
        cardPlasticType: er.cardPlasticType
      }));

    this.userTableItems = this.partnerCardIssueCriteriaStore.cardIssueConfigs.
      syncedUsers.map(this.mapSyncedUser).concat(
        this.partnerCardIssueCriteriaStore.cardIssueConfigs.unsyncedUsers.map(this.mapUnsyncedUser)
      );
  }

  // only show loader when its taking longer
  // than expected i.e. initial page load
  timeout = null;
  @Watch("partnerCardIssueCriteriaStore.fetchState")
  isLoadedChange () {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.timeout = setTimeout(() => {
      this.isLoaded = this.partnerCardIssueCriteriaStore.isLoaded;
    }, 500);
  }

  mounted () {
    this.onPartnerIdChanged();
    this.transformCardIssueConfigs();
    this.partnerCardIssueCriteriaStore.loadCardIssueCustomizationOptions();
  }

  canActivate () {
    const isCardIssueCustomizationLoaded = this.partnerCardIssueCriteriaStore.isCardIssueCustomizationLoaded(this.partnerIssueCardCriteriaIdNum);

    if (isCardIssueCustomizationLoaded) {
      return this.partnerCardIssueCriteriaStore.canActivateCardIssuance(this.partnerIssueCardCriteriaIdNum);
    }
  }

  get partnerIdNum (): number {
    return Number(this.partnerId);
  }

  get partnerName (): string {
    return this.partnerStore.partnerName(this.partnerId);
  }

  get partnerIssueCardCriteriaIdNum (): number {
    return this.partnerCardIssueCriteriaStore.cardIssueConfigs.partner.issueCardCriteriaId;
  }

  get partnerCardPlasticType (): CardPlasticType {
    return this.isPartnerConfigEnabled && this.partnerCardIssueCriteriaStore.cardIssueConfigs.partner.cardPlasticType;
  }

  get isPartnerConfigActive () {
    return this.partnerCardIssueCriteriaStore.cardIssueConfigs.partner !== null &&
    this.partnerCardIssueCriteriaStore.cardIssueConfigs.partner.dateEnabled !== null;
  }

  get isPartnerConfigEnabled () {
    return this.partnerCardIssueCriteriaStore.cardIssueConfigs.partner !== null;
  }

  plasticTypeOptions = [
    { value: CardPlasticType.MagneticStrip, text: "Magnetic Strip" },
    { value: CardPlasticType.Chip, text: "Chip" }
  ];

  askDeletePrCardIssueCriteria (issueCardCriteriaId: number) {
    if (
      confirm(`Are you sure you would like to remove card issuance for the entire partner, ${this.partnerName}? Enabled employers and individuals will still continue to receive cards`)
    ) {
      this.deleteCardIssueCriteria(issueCardCriteriaId);
    }
  }

  askDeleteErCardIssueCriteria (item: EmployerTableItem) {
    if (
      confirm(`Are you sure you would like to remove card issuance for the entire employer, ${item.employerName}? Enabled individuals will still continue to receive cards. If the partner is enabled all account holders will still receive cards.`)
    ) {
      this.deleteCardIssueCriteria(item.issueCardCriteriaId);
    }
  }

  askDeleteUserCardIssueCriteria (item: UserTableItem) {
    let userLabel = item.lastName ? `${item.firstName} ${item.lastName} ` : "";
    userLabel += `with last 4 SSN of ${item.userSsnLast4}`;
    if (
      confirm(`Are you sure you would like to remove card issuance for account holder ${userLabel}? Account holder will still receive a card if enabled at the employer or partner level.`)
    ) {
      this.deleteCardIssueCriteria(item.issueCardCriteriaId);
    }
  }

  deleteCardIssueCriteria (issueCardCriteriaId: number) {
    this.partnerCardIssueCriteriaStore.deleteCardIssueCriteria(issueCardCriteriaId)
    .then(() => {
      this.reload();
    });
  }

  activateCardIssueCriteria (issueCardCriteriaId: number) {
    if (confirm("Are you sure you want to activate this criteria? Account holders will start getting issued cards immediately using the current criteria settings. If you are not sure, hit Cancel, and check the criteria settings before enabling.")) {
      this.partnerCardIssueCriteriaStore.activateCardIssueCriteria(issueCardCriteriaId)
      .then(() => {
        this.reload();
      });
    }
  }

  addPartnerCardIssueCriteria () {
    const request: CreateCardIssueCriteriaRequest = {
      partnerId: this.partnerIdNum
    };

    this.partnerCardIssueCriteriaStore.createCardIssueCriteria(request)
    .then(() => {
      this.reload();
    });
  }

  askSetPartnerCardCriteriaCustomization (issueCardCriteriaId: number, plasticType: CardPlasticType) {
    const request: SetCardCriteriaCustomizationRequest = {
      default: this.createCardCriteriaCustomizationRequest (plasticType)
    };

    const isNotActiveYet = !this.isPartnerConfigActive;
    const oldOption = this.plasticTypeOptions.find(opt => opt.value === this.partnerCardPlasticType);
    const oldOptionName = oldOption ? oldOption.text : "current selection";
    const newOption = this.plasticTypeOptions.find(opt => opt.value === plasticType);
    const newOptionName = newOption ? newOption.text : plasticType;

    if (isNotActiveYet || confirm(
      `Are you sure you would like to change the plastic type of Partner ${this.partnerName} from ${oldOptionName} to ${newOptionName}?  Any newly detected accounts will be sent with new plastic type, but existing accounts will have the old plastic type.`
    )) {
      this.setCardCriteriaCustomization(issueCardCriteriaId, request);
    } else {
      this.selectedPartnerCardPlasticType = this.partnerCardPlasticType;
    }
  }

  askSetCardCriteriaCustomization (
    item: EmployerTableItem & UserTableItem,
    plasticType: CardPlasticType,
    audienceName: string,
  ) {
      const request: SetCardCriteriaCustomizationRequest = {
        default: this.createCardCriteriaCustomizationRequest (plasticType)
      };

      const isNotActiveYet = item.status !== "Active";
      const newOption = this.plasticTypeOptions.find(opt => opt.value === plasticType);
      const newOptionName = newOption ? newOption.text : plasticType;

      if (isNotActiveYet || confirm(
        `Are you sure you would like to change the plastic type of ${audienceName} to ${newOptionName}?  Any newly detected accounts will be sent with new plastic type, but existing accounts will have the old plastic type.`
      )) {
        this.setCardCriteriaCustomization(item.issueCardCriteriaId, request);
      } else {
        this.transformCardIssueConfigs();
      }
  }

  setCardCriteriaCustomization (issueCardCriteriaId: number, request: SetCardCriteriaCustomizationRequest) {
    this.partnerCardIssueCriteriaStore.setCardIssueCriteriaCustomization(issueCardCriteriaId, request)
      .then(() => {
        this.notifierStore.setSuccess("Card type saved.");
      })
      .catch(() => {
        this.notifierStore.setDanger("There was an error saving card type.");
      });
  }

  get employerTableFields () {
    return [
      { key: "employerId", lable: "EmployerId" },
      { key: "employerName", label: "Employer Name" },
      { key: "cardPlasticType", label: "Plastic Type" },
      { key: "status", label: "Status" },
      { key: "actions", label: "Actions" },
    ];
  }

  get userTableFields () {
    return [
      { key: "firstName", label: "First Name" },
      { key: "lastName", label: "Last Name" },
      { key: "userEmail", label: "User Email" },
      { key: "userSsnLast4", label: "User Ssn Last 4 digits" },
      { key: "employerId", label: "EmployerId" },
      { key: "employerName", label: "Employer Name" },
      { key: "cardPlasticType", label: "Plastic Type" },
      { key: "status", label: "Status" },
      { key: "actions", label: "Actions" }
    ];
  }

  mapUnsyncedUser (userConfig: CardIssueConfig): UserTableItem {
    return {
      firstName: "User Not Found",
      lastName: "",
      userEmail: "",
      userSsnLast4: userConfig.ssnLast4,
      employerId: userConfig.employerId,
      employerName: this.partnerCardIssueCriteriaStore.getEmployerName(userConfig.employerId),
      issueCardCriteriaId: userConfig.issueCardCriteriaId,
      dateEnabled: userConfig.dateEnabled,
      cardPlasticType: userConfig.cardPlasticType,
      status: this.partnerCardIssueCriteriaStore.getConfigStatus(userConfig)
    };
  }

  mapSyncedUser (userConfig: CardIssueConfig): UserTableItem {
    return {
      firstName: userConfig.firstName,
      lastName: userConfig.lastName,
      userEmail: userConfig.email,
      userSsnLast4: userConfig.ssnLast4,
      employerId: userConfig.employerId,
      employerName: this.partnerCardIssueCriteriaStore.getEmployerName(userConfig.employerId),
      issueCardCriteriaId: userConfig.issueCardCriteriaId,
      dateEnabled: userConfig.dateEnabled,
      cardPlasticType: userConfig.cardPlasticType,
      status: this.partnerCardIssueCriteriaStore.getConfigStatus(userConfig)
    };
  }

  reload () {
    this.partnerCardIssueCriteriaStore.reloadPartnerCardIssueStore();
  }

  createCardCriteriaCustomizationRequest (plasticType: CardPlasticType) {
    const model: CardCustomizationModel = {
      cardPlasticType: cardPlasticTypeValueMap[plasticType],
      primaryPersonalizedEmbossingText: null,
      phoneNumber: null,
      url: null,
      issuerDefinedCardLogoCode: null,
      cardCarrierFrontTemplate: null,
      cardActivationLabel: null,
      feeScheduleId: null,
      cardProvider: CardProvider.Fiserv,
      fundPurseId: null,
      plansThatReceiveCards: null,
      usersMovedToThisEmployerWillBeIssuedNewCards: false,
      employeeDisposition: EmployeeDisposition.Default,
      purseStackingOrderValueId: null,
      plasticRushCode: null
    };
    return model;
  }

  get hasEditPermission (): boolean {
    return this.rolePermissionsStore.userHasAdminPermission(UserPermission.AdminPortalCardSettingsEdit);
  }

  get hasCardSettingAccess (): boolean {
    return this.rolePermissionsStore.userHasAdminPermission(UserPermission.AdminPortalCardSettingsEdit) ||
        this.rolePermissionsStore.userHasAdminPermission(UserPermission.AdminPortalCardSettingsView);
  }
}
